import {
  TDeviceType,
  TPlatformFormFactor,
  TPlatformOS,
  getPlatformFormFactor,
  getPlatformLabel,
  getPlatformOS,
  isDesktopPlatform as detectIsDesktopPlatform,
  isMobilePlatform as detectIsMobilePlatform,
  isTabletPlatform as detectIsTabletPlatform,
} from '@/configs/responsive.config';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export interface IResponsiveStore {
  /**
   * State
   */
  deviceType: TDeviceType | null;
  dimensions: {
    width: number;
    height: number;
  };
  isSmallMobile: boolean;
  isMobile: boolean;
  isAnyMobile: boolean;
  isTablet: boolean;
  isTabletLandscape: boolean;
  isAnyTablet: boolean;
  isLaptop: boolean;
  isDesktop: boolean;
  isMonitor2k: boolean;
  isMonitor4k: boolean;

  // Platform (OS + form-factor)
  platformOS: TPlatformOS;
  platformFormFactor: TPlatformFormFactor;
  platformLabel: string; // e.g. "ios-tablet"
  isApplePlatform: boolean;
  isAndroidPlatform: boolean;
  isWindowsPlatform: boolean;
  isLinuxPlatform: boolean;
  isChromeOSPlatform: boolean;
  isUnknownPlatform: boolean;
  isMobilePlatform: boolean;
  isTabletPlatform: boolean;
  isDesktopPlatform: boolean;

  /**
   * Actions
   */
  actions: {
    setDeviceType: (deviceType: TDeviceType) => void;
    setDimensions: (dimensions: { width: number; height: number }) => void;
    setPlatform: (args?: { ua?: string; width?: number }) => void;
  };
}

export const responsiveStore = create<IResponsiveStore>((set) => ({
  /**
   * State
   */
  deviceType: null,
  dimensions: {
    width: 0,
    height: 0,
  },
  isSmallMobile: false,
  isMobile: false,
  isAnyMobile: false,
  isTablet: false,
  isTabletLandscape: false,
  isAnyTablet: false,
  isLaptop: false,
  isDesktop: false,
  isMonitor2k: false,
  isMonitor4k: false,

  // Platform defaults (SSR-safe)
  platformOS: 'unknown',
  platformFormFactor: 'desktop',
  platformLabel: 'unknown-desktop',
  isApplePlatform: false,
  isAndroidPlatform: false,
  isWindowsPlatform: false,
  isLinuxPlatform: false,
  isChromeOSPlatform: false,
  isUnknownPlatform: true,
  isMobilePlatform: false,
  isTabletPlatform: false,
  isDesktopPlatform: true,

  /**
   * Actions
   */
  actions: {
    setDimensions: (dimensions: { width: number; height: number }) => {
      set((prev) => {
        const sameWidth = prev.dimensions.width === dimensions.width;
        const sameHeight = prev.dimensions.height === dimensions.height;

        // No changes, return previous state to prevent unnecessary re-renders
        if (sameWidth && sameHeight) return prev;
        return { dimensions };
      });
    },
    setDeviceType: (deviceType: TDeviceType) => {
      set((prev) => {
        // No changes, return previous state to prevent unnecessary re-renders
        if (prev.deviceType === deviceType) return prev;

        const isSmallMobile = deviceType === 'small-mobile';
        const isMobile = deviceType === 'mobile';
        const isTablet = deviceType === 'tablet';
        const isTabletLandscape = deviceType === 'tablet-landscape';
        const isLaptop = deviceType === 'laptop';
        const isDesktop = deviceType === 'desktop';
        const isMonitor2k = deviceType === 'monitor-2k';
        const isMonitor4k = deviceType === 'monitor-4k';

        return {
          deviceType,
          isSmallMobile,
          isMobile,
          isAnyMobile: isMobile || isSmallMobile,
          isTablet,
          isTabletLandscape,
          isAnyTablet: isTablet || isTabletLandscape,
          isLaptop,
          isDesktop,
          isMonitor2k,
          isMonitor4k,
        };
      });
    },
    setPlatform: (args?: { ua?: string; width?: number }) => {
      const ua = args?.ua;
      const width = args?.width;

      const platformOS = getPlatformOS(ua);
      const platformFormFactor = getPlatformFormFactor(ua, width);
      const platformLabel = getPlatformLabel(ua, width);

      const isMobilePlatform = detectIsMobilePlatform(ua, width);
      const isTabletPlatform = detectIsTabletPlatform(ua, width);
      const isDesktopPlatform = detectIsDesktopPlatform(ua, width);

      const isApplePlatform = platformOS === 'ios' || platformOS === 'macos';
      const isAndroidPlatform = platformOS === 'android';
      const isWindowsPlatform = platformOS === 'windows';
      const isLinuxPlatform = platformOS === 'linux';
      const isChromeOSPlatform = platformOS === 'chromeos';
      const isUnknownPlatform = platformOS === 'unknown';

      set((prev) => {
        const noChanges =
          prev.platformOS === platformOS &&
          prev.platformFormFactor === platformFormFactor &&
          prev.platformLabel === platformLabel &&
          prev.isApplePlatform === isApplePlatform &&
          prev.isAndroidPlatform === isAndroidPlatform &&
          prev.isWindowsPlatform === isWindowsPlatform &&
          prev.isLinuxPlatform === isLinuxPlatform &&
          prev.isChromeOSPlatform === isChromeOSPlatform &&
          prev.isUnknownPlatform === isUnknownPlatform &&
          prev.isMobilePlatform === isMobilePlatform &&
          prev.isTabletPlatform === isTabletPlatform &&
          prev.isDesktopPlatform === isDesktopPlatform;

        // No changes, return previous state to prevent unnecessary re-renders
        if (noChanges) return prev;

        return {
          platformOS,
          platformFormFactor,
          platformLabel,
          isApplePlatform,
          isAndroidPlatform,
          isWindowsPlatform,
          isLinuxPlatform,
          isChromeOSPlatform,
          isUnknownPlatform,
          isMobilePlatform,
          isTabletPlatform,
          isDesktopPlatform,
        };
      });
    },
  },
}));

/**
 * Selectors
 */
export const useDeviceTypeState = () => responsiveStore(useShallow((state) => state.deviceType));
export const useDimensionsState = () => responsiveStore(useShallow((state) => state.dimensions));
export const useIsSmallMobileState = () => responsiveStore(useShallow((state) => state.isSmallMobile));
export const useIsMobileState = () => responsiveStore(useShallow((state) => state.isMobile));
export const useIsAnyMobileState = () => responsiveStore(useShallow((state) => state.isAnyMobile));
export const useIsTabletState = () => responsiveStore(useShallow((state) => state.isTablet));
export const useIsTabletLandscapeState = () => responsiveStore(useShallow((state) => state.isTabletLandscape));
export const useIsAnyTabletState = () => responsiveStore(useShallow((state) => state.isAnyTablet));
export const useIsLaptopState = () => responsiveStore(useShallow((state) => state.isLaptop));
export const useIsDesktopState = () => responsiveStore(useShallow((state) => state.isDesktop));
export const useIsMonitor2kState = () => responsiveStore(useShallow((state) => state.isMonitor2k));
export const useIsMonitor4kState = () => responsiveStore(useShallow((state) => state.isMonitor4k));

// Platform selectors
export const useIsApplePlatformState = () => responsiveStore(useShallow((state) => state.isApplePlatform));
export const useIsAndroidPlatformState = () => responsiveStore(useShallow((state) => state.isAndroidPlatform));
export const useIsWindowsPlatformState = () => responsiveStore(useShallow((state) => state.isWindowsPlatform));
export const useIsLinuxPlatformState = () => responsiveStore(useShallow((state) => state.isLinuxPlatform));
export const useIsChromeOSPlatformState = () => responsiveStore(useShallow((state) => state.isChromeOSPlatform));
export const useIsUnknownPlatformState = () => responsiveStore(useShallow((state) => state.isUnknownPlatform));
export const usePlatformOSState = () => responsiveStore(useShallow((state) => state.platformOS));
export const usePlatformFormFactorState = () => responsiveStore(useShallow((state) => state.platformFormFactor));
export const usePlatformLabelState = () => responsiveStore(useShallow((state) => state.platformLabel));
export const useIsMobilePlatformState = () => responsiveStore(useShallow((state) => state.isMobilePlatform));
export const useIsTabletPlatformState = () => responsiveStore(useShallow((state) => state.isTabletPlatform));
export const useIsDesktopPlatformState = () => responsiveStore(useShallow((state) => state.isDesktopPlatform));

/**
 * Actions
 */
export const useResponsiveStoreActions = () => responsiveStore(useShallow((state) => state.actions));
